/**
 * Push Notification Service — Web Push subscription management.
 *
 * Handles:
 * - Requesting notification permission
 * - Subscribing to push via the Service Worker
 * - Persisting subscription to Supabase (push_subscriptions table)
 * - Unsubscribing
 *
 * The VAPID public key must be set in environment as VITE_VAPID_PUBLIC_KEY.
 */

import { supabase } from "./core.js";
import { logError, logWarn } from "../shared/logger.js";

// ── Types ──────────────────────────────────────────────────────────

export interface PushSubscriptionRecord {
  id: string;
  user_id: string;
  client_id: string | null;
  endpoint: string;
  created_at: string;
}

export type PushPermissionState = "granted" | "denied" | "default" | "unsupported";

// ── Helpers ────────────────────────────────────────────────────────

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Check current push notification permission state.
 */
export function getPushPermissionState(): PushPermissionState {
  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    return "unsupported";
  }
  return Notification.permission as PushPermissionState;
}

/**
 * Check if push is supported in this browser.
 */
export function isPushSupported(): boolean {
  return "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
}

/**
 * Check if user already has an active push subscription.
 */
export async function hasActiveSubscription(): Promise<boolean> {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}

/**
 * Subscribe to push notifications.
 * 1. Request permission
 * 2. Get PushSubscription from SW
 * 3. Save to Supabase
 */
export async function subscribeToPush(clientId?: string): Promise<boolean> {
  try {
    if (!isPushSupported()) {
      logWarn("pushService", "Push not supported in this browser");
      return false;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      logWarn("pushService", `Permission ${permission}`);
      return false;
    }

    // Get service worker registration
    const reg = await navigator.serviceWorker.ready;

    // Check for existing subscription
    let subscription = await reg.pushManager.getSubscription();

    // Create new subscription if none exists
    if (!subscription) {
      if (!VAPID_PUBLIC_KEY) {
        logError("pushService", new Error("VAPID public key not configured"));
        return false;
      }
      subscription = await reg.pushManager.subscribe({
        userVisuallyPrompted: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      } as PushSubscriptionOptionsInit);
    }

    // Extract keys
    const json = subscription.toJSON();
    const p256dh = json.keys?.p256dh || "";
    const authKey = json.keys?.auth || "";

    // Save to Supabase
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase.from("push_subscriptions").upsert(
      {
        user_id: user.id,
        client_id: clientId || null,
        endpoint: subscription.endpoint,
        p256dh,
        auth_key: authKey,
        user_agent: navigator.userAgent,
      },
      { onConflict: "user_id,endpoint" }
    );

    if (error) {
      logError("pushService.subscribe", error);
      return false;
    }

    logWarn("pushService", "Subscribed successfully");
    return true;
  } catch (e) {
    logError("pushService.subscribe", e as Error);
    return false;
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();

    if (subscription) {
      // Remove from Supabase
      await supabase.from("push_subscriptions").delete().eq("endpoint", subscription.endpoint);

      // Unsubscribe from browser
      await subscription.unsubscribe();
    }

    logWarn("pushService", "Unsubscribed successfully");
    return true;
  } catch (e) {
    logError("pushService.unsubscribe", e as Error);
    return false;
  }
}
